import {
  isReadyMessage,
  isSaveAndExitResponse,
  isEditingStartedMessage,
  isSetBaseLineResponse,
  SaveAndExitResponse,
  EditingStartedMessage,
  SetBaseLineResponse,
} from "../bintactMapMessages";

export interface MessageHandlerCallbacks {
  onReady: () => void;
  onSaveResponse: (response: SaveAndExitResponse) => void;
  onEditingStarted: (message: EditingStartedMessage) => void;
  onSetBaseLineResponse: (response: SetBaseLineResponse) => void;
}

/**
 * Creates message handlers for Bintact map communication
 * @param callbacks - Callback functions to handle different message types
 * @returns An object with handler functions
 */
export function createBintactMessageHandler(callbacks: MessageHandlerCallbacks) {
  /**
   * Handles incoming messages from the map service
   * @param data - The message data received from the iframe
   * @returns true if the message was handled, false otherwise
   */
  const handleMessage = (data: unknown): boolean => {
    // Handle ready message (string)
    if (isReadyMessage(data)) {
      callbacks.onReady();
      return true;
    }

    // Handle structured messages (objects)
    if (typeof data === "object" && data !== null) {
      const obj = data as Record<string, unknown>;

      // Check if this is a nested message structure
      // Some messages come wrapped in a message property
      const messageValue = obj.message;

      // Handle SaveAndExitResponse
      if (isSaveAndExitResponse(data)) {
        callbacks.onSaveResponse(data);
        return true;
      }

      // Handle nested SaveAndExitResponse
      if (
        messageValue &&
        typeof messageValue === "object" &&
        isSaveAndExitResponse(messageValue)
      ) {
        callbacks.onSaveResponse(messageValue);
        return true;
      }

      // Handle EditingStartedMessage
      if (isEditingStartedMessage(data)) {
        callbacks.onEditingStarted(data);
        return true;
      }

      // Handle nested EditingStartedMessage
      if (
        messageValue &&
        typeof messageValue === "object" &&
        isEditingStartedMessage(messageValue)
      ) {
        callbacks.onEditingStarted(messageValue);
        return true;
      }

      // Handle SetBaseLineResponse
      if (isSetBaseLineResponse(data)) {
        callbacks.onSetBaseLineResponse(data);
        return true;
      }

      // Handle nested SetBaseLineResponse
      if (
        messageValue &&
        typeof messageValue === "object" &&
        isSetBaseLineResponse(messageValue)
      ) {
        callbacks.onSetBaseLineResponse(messageValue);
        return true;
      }
    }

    return false;
  };

  return {
    handleMessage,
  };
}

